import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Chip, Button, Icon } from '@ann/web-components';
import {
  ActionBarRow,
  ChipsGroup,
  ButtonsGroup,
  SearchInputWrap,
  SearchInputWrapper,
  SearchIconLeft,
  SearchInputWithClearWrap,
  SearchInputField,
  SearchClearButton,
} from './ContactsActionBar.styled';

const CONTACT_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'customer', label: 'Customer' },
  { value: 'company', label: 'Company' },
  { value: 'household', label: 'Household' },
];

const SEARCH_DEBOUNCE_MS = 500;

const ContactsActionBar = ({
  contactTypeFilter = 'all',
  onContactTypeChange,
  searchValue = '',
  onSearchChange,
  onFiltersClick,
  onCreateContact,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(searchValue || '');
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const hasSearchValue = localSearchValue != null && String(localSearchValue).length > 0;

  useEffect(() => {
    setLocalSearchValue(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleSearchInputChange = useCallback(
    (value) => {
      setLocalSearchValue(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange?.(value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [onSearchChange]
  );

  const handleSearchClearOrClose = useCallback(() => {
    if (hasSearchValue) {
      setLocalSearchValue('');
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      onSearchChange?.('');
      inputRef.current?.focus();
    } else {
      setIsExpanded(false);
    }
  }, [hasSearchValue, onSearchChange]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        if (hasSearchValue) {
          setLocalSearchValue('');
          if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
          onSearchChange?.('');
          inputRef.current?.focus();
        } else {
          setIsExpanded(false);
        }
      }
    },
    [hasSearchValue, onSearchChange]
  );

  return (
    <ActionBarRow>
      <ChipsGroup>
        {CONTACT_TYPES.map(({ value, label }) => (
          <Chip
            key={value}
            text={label}
            variant={contactTypeFilter === value ? 'primary' : 'secondary'}
            size="medium"
            onClick={() => onContactTypeChange?.(value)}
          />
        ))}
      </ChipsGroup>
      <ButtonsGroup>
        {isExpanded ? (
          <SearchInputWrap>
            <SearchInputWrapper>
              <SearchIconLeft aria-hidden>
                <Icon name="search" size="custom" width="16px" height="16px" />
              </SearchIconLeft>
              <SearchInputWithClearWrap>
                <SearchInputField
                  ref={inputRef}
                  type="text"
                  value={localSearchValue}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  placeholder="Search Contacts..."
                  autoFocus
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Search contacts"
                />
                <SearchClearButton
                  type="button"
                  onClick={handleSearchClearOrClose}
                  aria-label={hasSearchValue ? 'Clear search' : 'Close search'}
                  tabIndex={0}
                >
                  <Icon name="close" size="small" />
                </SearchClearButton>
              </SearchInputWithClearWrap>
            </SearchInputWrapper>
          </SearchInputWrap>
        ) : (
          <Button
            icon="search"
            size="small"
            type="secondary"
            pill
            onClick={() => setIsExpanded(true)}
            aria-label="Search contacts"
          />
        )}
        <Button
          label="Filters"
          iconRight="tune"
          size="small"
          type="secondary"
          pill
          onClick={onFiltersClick}
          disabled
        />
        <Button
          label="Create contact"
          iconLeft="plus"
          size="small"
          type="primary"
          onClick={onCreateContact}
          disabled
        />
      </ButtonsGroup>
    </ActionBarRow>
  );
};

export default ContactsActionBar;
